import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InsuranceArticleSidebar from './InsuranceArticleSidebar';
import { renderArticleText } from '../utils/renderArticleText';
import type { InsuranceProductConfig } from '../data/insuranceArticleTypes';

type KnowledgeArticlePageProps = {
  config: InsuranceProductConfig;
  slug: string;
};

function KnowledgeArticlePage({ config, slug }: KnowledgeArticlePageProps) {
  const navigate = useNavigate();
  const article = config.contentBySlug[slug];
  const activeHref = `${config.basePath}/${slug}`;

  useEffect(() => {
    if (!article) return;

    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = article.metaTitle;

    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', article.metaDescription);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, [article]);

  if (!article) {
    return (
      <div className="container">
        <div className="card">
          <div className="card-body">
            <h2>Article not found</h2>
            <p style={{ marginBottom: '1rem' }}>That guide is not available yet.</p>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/knowledge-base')}>
              Back to knowledge base
            </button>
          </div>
        </div>
      </div>
    );
  }

  const startQualify = () => {
    sessionStorage.setItem('insuranceType', config.qualifyInsuranceType);
    navigate('/qualify');
  };

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <InsuranceArticleSidebar
          sidebarTitle={config.sidebarTitle}
          categories={config.categories}
          activeHref={activeHref}
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => navigate('/knowledge-base')}
              style={{ marginBottom: '1rem' }}
            >
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>{article.title}</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>{renderArticleText(article.subtitle)}</p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>
              Last modified: {config.lastModified}
            </p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              {article.sections.map((section) => (
                <div key={section.title}>
                  <h3 style={{ marginBottom: '0.75rem', marginTop: section.title === article.sections[0]?.title ? 0 : '1.5rem' }}>
                    {section.title}
                  </h3>
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)} style={{ marginBottom: section.bullets || section.numbered || section.table ? '0.75rem' : 0 }}>
                      {renderArticleText(paragraph)}
                    </p>
                  ))}
                  {section.bullets && (
                    <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                      {section.bullets.map((item, index) => (
                        <li key={item.slice(0, 40)} style={{ marginBottom: index === section.bullets!.length - 1 ? 0 : '0.5rem' }}>
                          {renderArticleText(item)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.numbered && (
                    <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                      {section.numbered.map((item, index) => (
                        <li key={item.slice(0, 40)} style={{ marginBottom: index === section.numbered!.length - 1 ? 0 : '0.5rem' }}>
                          {renderArticleText(item)}
                        </li>
                      ))}
                    </ol>
                  )}
                  {section.table && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                        <thead>
                          <tr>
                            {section.table.headers.map((header) => (
                              <th
                                key={header}
                                style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.table.rows.map((row, rowIndex) => (
                            <tr key={row.join('-')}>
                              {row.map((cell, cellIndex) => (
                                <td
                                  key={`${row[0]}-${cellIndex}`}
                                  style={{
                                    borderBottom:
                                      rowIndex === section.table!.rows.length - 1
                                        ? undefined
                                        : '1px solid var(--color-grey)',
                                    padding: '0.6rem',
                                  }}
                                >
                                  {renderArticleText(cell)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </article>

          <section
            className="card mb-4"
            style={{
              borderColor: 'var(--color-green)',
              background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>
                {article.ctaHeading ?? `Start ${config.sidebarTitle.toLowerCase()} with GetSkimp`}
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                {article.ctaBody ?? 'Compare coverage options and get a free quote in minutes.'}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={startQualify}
                style={{ fontWeight: 700, paddingInline: '1.4rem' }}
              >
                Get started now
              </button>
            </div>
          </section>

          <section
            className="card"
            style={{
              borderColor: '#FBBF24',
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>
                {article.agentHeading ?? 'Want to talk through your options?'}
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                {article.agentBody ?? 'An agent can answer questions and help you choose the right coverage.'}
              </p>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/help')}
                style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}
              >
                Speak to an agent
              </button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default KnowledgeArticlePage;
